import { Field, useField } from 'formik';
import { FocusEvent, useCallback, memo, type MouseEvent } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, TextField, Tooltip } from '@mui/material';

import { useLatest } from 'react-use';
import {
    InterviewLanguage,
    MultipleChoiceQuestion,
    MultipleChoiceQuestionDetail,
} from '@synergetx/puppet-interview-types';
import ChoiceActionButtons from './ChoiceActionButtons';
import { move, remove } from 'utils/arrayHelpers';
import { OtherOptionPreview } from './OtherOptionPreview';
import FieldToggle from './FieldToggle';

interface Props {
    position: number;
    language: InterviewLanguage;
}

const MultipleChoiceOptionFields = ({ position, language }: Props) => {
    const [{ value: question }, , { setValue: setQuestion }] = useField<
        MultipleChoiceQuestionDetail['localeContent'][typeof language]
    >(`questions[${position}].localeContent.[${language}]`);

    const [{ value: choiceQuestion }, ,] = useField<MultipleChoiceQuestion>(
        `questions[${position}]`,
    );

    const hasChoices = question && 'choices' in question;
    const hasOtherOption = !!question?.hasOtherOption;

    const setChoices = useCallback(
        (choices: string[] | undefined) => {
            if (hasChoices) {
                setQuestion({
                    ...question,
                    choices: choices || [],
                });
            }
        },
        [hasChoices, question, setQuestion],
    );

    const addChoiceToQuestion = useLatest(
        (event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
            event.preventDefault();
            event.stopPropagation();
            setChoices(question?.choices?.concat(''));
        },
    ).current;

    const moveChoice = useCallback(
        (index: number, newIndex: number) => {
            if (hasChoices) {
                setChoices(move(question?.choices || [], index, newIndex));
            }
        },
        [hasChoices, question, setChoices],
    );

    const isNavDisabled = useCallback(
        (index: number, direction: 'up' | 'down'): boolean => {
            if (!hasChoices) {
                return false;
            }

            const isLastQuestion =
                !question.choices?.length || index === question.choices?.length - 1;
            const isFirstQuestion = index === 0;

            if (direction === 'up') {
                return isFirstQuestion;
            }

            if (direction === 'down') {
                return isLastQuestion;
            }

            return false;
        },
        [hasChoices, question],
    );

    return hasChoices ? (
        <>
            <FieldToggle
                question={choiceQuestion}
                position={position}
                fieldName={'selectMultiple'}
                label="Allow multiple selections?"
                theming={{ marginBottom: '1rem' }}
            />
            <fieldset
                style={{
                    padding: '1rem',
                    marginTop: '1rem',
                    borderColor: 'rgba(0,0,0,.2)',
                    borderRadius: '8px',
                    borderWidth: '0.2px',
                }}
            >
                <legend>Options</legend>
                {question.choices.map((option: string, i: number) => (
                    <div
                        style={{
                            marginLeft: '2rem',
                            marginBottom: '1rem',
                            display: 'flex',
                            flexDirection: 'row',
                            position: 'relative',
                        }}
                        // Index key on purpose: bare strings, and content keys collide when blank.
                        key={`option-${i}`}
                    >
                        <ChoiceActionButtons
                            moveUpDisabled={isNavDisabled(i, 'up')}
                            moveDownDisabled={isNavDisabled(i, 'down')}
                            canDeleteChoice={question.choices.length > 1}
                            removeOption={() => setChoices(remove(question.choices, i))}
                            moveDown={() => moveChoice(i, i + 1)}
                            moveUp={() => moveChoice(i, i - 1)}
                        />
                        <Field
                            as={TextField}
                            fullWidth
                            name={`questions[${position}].localeContent.[${language}].choices[${i}]`}
                            label={`Option ${i + 1}`}
                            variant="outlined"
                            slotProps={{
                                input: {
                                    style: { minWidth: '15rem' },
                                    placeholder: 'Enter option',
                                    onFocus: (
                                        e: FocusEvent<
                                            HTMLInputElement | HTMLTextAreaElement,
                                            Element
                                        >,
                                    ) => {
                                        e.target.select();
                                    },
                                    onClick: (
                                        e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
                                    ) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    },
                                },
                            }}
                        />
                    </div>
                ))}
                {question && hasOtherOption ? (
                    <div style={{ marginLeft: '3.5rem' }}>
                        <OtherOptionPreview
                            position={position}
                            optionNumber={(question.choices && question.choices?.length + 1) || 1}
                        />
                    </div>
                ) : null}
                <Button
                    sx={{ marginLeft: '2rem' }}
                    variant="text"
                    size="small"
                    color="secondary"
                    onClick={addChoiceToQuestion}
                    startIcon={<AddIcon />}
                >
                    Add Option
                </Button>
                <Tooltip
                    title={
                        hasOtherOption
                            ? `Removes "Free Form" option from this question`
                            : `This allows users to enter in their own response for this option`
                    }
                >
                    <Button
                        variant="text"
                        color="secondary"
                        size="small"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setQuestion({
                                ...question,
                                hasOtherOption: !question?.hasOtherOption,
                            });
                        }}
                        startIcon={hasOtherOption ? <RemoveIcon /> : <AddIcon />}
                    >
                        {hasOtherOption
                            ? 'Remove "Free Form" Response'
                            : 'Add "Free Form" Response'}
                    </Button>
                </Tooltip>
            </fieldset>
        </>
    ) : null;
};

export default memo(MultipleChoiceOptionFields);
